import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  EDiscountAssignmentType,
  EDiscountType,
  type IDiscount,
  type IDiscountTargetCriteria,
  type IDiscountType,
} from '@mahawi/eshop-common/src/types';
import {
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getCountryOptions } from '../../fragments/get-country-options';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { categoriesLoadAll } from '../../reducers/categories/reducer';
import { countriesLoadAll } from '../../reducers/countries/reducer';
import { discountSave, discountUpdate } from '../../reducers/discount/reducer';
import { productsLoadAllShort } from '../../reducers/products/reducer';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import CustomerPicker from './customer-picker';

export enum ETargetingMode {
  NONE = 'none',
  INDIVIDUAL = 'individual',
  COUNTRY = 'country',
  ORDER_HISTORY = 'order-history',
}

export enum EProductTargetMode {
  NONE = 'none',
  PRODUCT = 'product',
  CATEGORY = 'category',
}

interface IDiscountFormValues {
  discountType: { uuid: string };
  value: number;
  description: string;
  code?: string;
  validFrom?: Dayjs;
  validTo?: Dayjs;
  multiUse?: boolean;
  singleUsePerCustomer?: boolean;
  maxRedemptions?: number;
  combinable?: boolean;
  targetingMode: ETargetingMode;
  individualCustomerUuids?: string[];
  countryTypeIsoCode?: string;
  minOrderCount?: number;
  orderCountWindowDays?: number;
  productTargetMode?: EProductTargetMode;
  productUuid?: string;
  categoryUuid?: string;
  productWindowDays?: number;
  minOrderValue?: number;
  eligibleCountryTypeIsoCodes?: string[];
  shippingFreeWeightKg?: number;
  referrerRewardDiscountTypeUuid?: string;
  referrerRewardValue?: number;
  referralMinOrderValue?: number;
  referralValidationWindowDays?: number;
}

function targetingModeFromDiscount(discount: IDiscount): ETargetingMode {
  if (discount.assignmentType === EDiscountAssignmentType.INDIVIDUAL) {
    return ETargetingMode.INDIVIDUAL;
  }

  if (discount.assignmentType === EDiscountAssignmentType.SEGMENT) {
    return discount.targetCriteria?.countryTypeIsoCode !== undefined &&
      discount.targetCriteria.minOrderCount === undefined
      ? ETargetingMode.COUNTRY
      : ETargetingMode.ORDER_HISTORY;
  }

  return ETargetingMode.NONE;
}

function describeTargeting(discount: IDiscount): string {
  if (discount.assignmentType === EDiscountAssignmentType.INDIVIDUAL) {
    return `Specific customers (${discount.assignedCount} assigned) — add more via "Assign to customers"`;
  }

  if (discount.assignmentType !== EDiscountAssignmentType.SEGMENT) {
    return 'Public (anyone can use)';
  }

  const criteria: string[] = [];
  const targetCriteria: IDiscountTargetCriteria | null =
    discount.targetCriteria;

  if (targetCriteria?.countryTypeIsoCode) {
    criteria.push(`country ${targetCriteria.countryTypeIsoCode}`);
  }

  if (targetCriteria?.minOrderCount) {
    criteria.push(
      `at least ${targetCriteria.minOrderCount} orders in the last ${targetCriteria.orderCountWindowDays} days`,
    );
  }

  if (targetCriteria?.productId) {
    criteria.push(
      `ordered a specific product in the last ${targetCriteria.productWindowDays} days`,
    );
  }

  if (targetCriteria?.categoryId) {
    criteria.push(
      `ordered from a specific category in the last ${targetCriteria.productWindowDays} days`,
    );
  }

  const summary = `Segment (${discount.assignedCount} assigned)`;

  return criteria.length > 0 ? `${summary}: ${criteria.join(', ')}` : summary;
}

function DiscountForm({
  open,
  onClose,
  editingDiscount,
  discountTypes,
  inProcess,
}: {
  open: boolean;
  onClose: () => void;
  editingDiscount: IDiscount | null;
  discountTypes: IDiscountType[] | undefined;
  inProcess: boolean;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const countries = useSelector((s: RootState) => s.Countries.countries);
  const productsShort = useSelector((s: RootState) => s.Products.productsShort);
  const categories = useSelector((s: RootState) => s.Categories.categories);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const [form] = Form.useForm<IDiscountFormValues>();
  const multiUseWatch: boolean = Form.useWatch('multiUse', form) ?? false;
  const targetingModeWatch: ETargetingMode =
    Form.useWatch('targetingMode', form) ?? ETargetingMode.NONE;
  const productTargetModeWatch: EProductTargetMode =
    Form.useWatch('productTargetMode', form) ?? EProductTargetMode.NONE;
  const discountTypeUuidWatch: string | undefined = Form.useWatch(
    ['discountType', 'uuid'],
    form,
  );
  const isEditing: boolean = !!editingDiscount;
  const isTargeted: boolean =
    !isEditing && targetingModeWatch !== ETargetingMode.NONE;
  const isMultiUse: boolean = editingDiscount
    ? editingDiscount.multiUse
    : multiUseWatch || isTargeted;
  const selectedDiscountTypeCode: string | undefined = editingDiscount
    ? editingDiscount.discountType.code
    : discountTypes?.find((dt) => dt.uuid === discountTypeUuidWatch)?.code;
  const isShippingType: boolean =
    selectedDiscountTypeCode === EDiscountType.SHIPPING;
  const isReferralType: boolean =
    selectedDiscountTypeCode === EDiscountType.REFERRAL;
  const referrerRewardDiscountTypeOptions = discountTypes?.filter(
    (dt) =>
      dt.code !== EDiscountType.SHIPPING && dt.code !== EDiscountType.REFERRAL,
  );

  useEffect(() => {
    if (open && !countries) {
      dispatch(countriesLoadAll());
    }
  }, [open, countries, dispatch]);

  useEffect(() => {
    if (open && !productsShort) {
      dispatch(productsLoadAllShort());
    }
  }, [open, productsShort, dispatch]);

  useEffect(() => {
    if (open && !categories) {
      dispatch(categoriesLoadAll());
    }
  }, [open, categories, dispatch]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (editingDiscount) {
      form.setFieldsValue({
        discountType: { uuid: editingDiscount.discountType.uuid },
        value: editingDiscount.value,
        description: editingDiscount.description || '',
        code: editingDiscount.code,
        validFrom: editingDiscount.validFrom
          ? dayjs(editingDiscount.validFrom)
          : undefined,
        validTo: editingDiscount.validTo
          ? dayjs(editingDiscount.validTo)
          : undefined,
        multiUse: editingDiscount.multiUse,
        singleUsePerCustomer: editingDiscount.singleUsePerCustomer,
        maxRedemptions: editingDiscount.maxRedemptions ?? undefined,
        combinable: editingDiscount.combinable,
        targetingMode: targetingModeFromDiscount(editingDiscount),
        countryTypeIsoCode: editingDiscount.targetCriteria?.countryTypeIsoCode,
        minOrderCount: editingDiscount.targetCriteria?.minOrderCount,
        orderCountWindowDays:
          editingDiscount.targetCriteria?.orderCountWindowDays,
        productWindowDays: editingDiscount.targetCriteria?.productWindowDays,
        minOrderValue: editingDiscount.minOrderValue ?? undefined,
        eligibleCountryTypeIsoCodes:
          editingDiscount.eligibleCountryTypeIsoCodes ?? undefined,
        shippingFreeWeightKg: editingDiscount.shippingFreeWeightKg ?? undefined,
        referrerRewardDiscountTypeUuid:
          editingDiscount.referrerRewardDiscountType?.uuid ?? undefined,
        referrerRewardValue: editingDiscount.referrerRewardValue ?? undefined,
        referralMinOrderValue:
          editingDiscount.referralMinOrderValue ?? undefined,
        referralValidationWindowDays:
          editingDiscount.referralValidationWindowDays ?? undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        value: 3,
        singleUsePerCustomer: true,
        targetingMode: ETargetingMode.NONE,
        productTargetMode: EProductTargetMode.NONE,
      });
    }
  }, [open, editingDiscount, form]);

  function buildTargetingPayload(values: IDiscountFormValues): {
    assignmentType: EDiscountAssignmentType;
    targetCriteria?: IDiscountTargetCriteria | null;
    productUuid?: string;
    categoryUuid?: string;
    individualCustomerUuids?: string[];
  } {
    if (values.targetingMode === ETargetingMode.INDIVIDUAL) {
      return {
        assignmentType: EDiscountAssignmentType.INDIVIDUAL,
        targetCriteria: null,
        individualCustomerUuids: values.individualCustomerUuids ?? [],
      };
    }

    if (values.targetingMode === ETargetingMode.COUNTRY) {
      return {
        assignmentType: EDiscountAssignmentType.SEGMENT,
        targetCriteria: { countryTypeIsoCode: values.countryTypeIsoCode },
      };
    }

    if (values.targetingMode === ETargetingMode.ORDER_HISTORY) {
      const hasProductFilter: boolean =
        values.productTargetMode !== EProductTargetMode.NONE;

      return {
        assignmentType: EDiscountAssignmentType.SEGMENT,
        targetCriteria: {
          countryTypeIsoCode: values.countryTypeIsoCode || undefined,
          minOrderCount: values.minOrderCount,
          orderCountWindowDays: values.minOrderCount
            ? values.orderCountWindowDays
            : undefined,
          productWindowDays: hasProductFilter
            ? values.productWindowDays
            : undefined,
        },
        productUuid:
          values.productTargetMode === EProductTargetMode.PRODUCT
            ? values.productUuid
            : undefined,
        categoryUuid:
          values.productTargetMode === EProductTargetMode.CATEGORY
            ? values.categoryUuid
            : undefined,
      };
    }

    return {
      assignmentType: EDiscountAssignmentType.NONE,
      targetCriteria: null,
    };
  }

  const handleFinish = (values: IDiscountFormValues): void => {
    if (editingDiscount) {
      dispatch(
        discountUpdate({
          uuid: editingDiscount.uuid,
          value: values.value,
          description: values.description,
          validFrom: values.validFrom ? values.validFrom.toDate() : null,
          validTo: values.validTo ? values.validTo.endOf('day').toDate() : null,
          maxRedemptions: editingDiscount.multiUse
            ? (values.maxRedemptions ?? null)
            : undefined,
          singleUsePerCustomer: editingDiscount.multiUse
            ? (values.singleUsePerCustomer ?? true)
            : undefined,
          combinable: editingDiscount.multiUse
            ? (values.combinable ?? false)
            : undefined,
          minOrderValue: values.minOrderValue ?? null,
          eligibleCountryTypeIsoCodes:
            values.eligibleCountryTypeIsoCodes ?? null,
          shippingFreeWeightKg: isShippingType
            ? (values.shippingFreeWeightKg ?? null)
            : null,
          referrerRewardDiscountTypeUUID: isReferralType
            ? values.referrerRewardDiscountTypeUuid
            : undefined,
          referrerRewardValue: isReferralType
            ? (values.referrerRewardValue ?? null)
            : null,
          referralMinOrderValue: isReferralType
            ? (values.referralMinOrderValue ?? null)
            : null,
          referralValidationWindowDays: isReferralType
            ? (values.referralValidationWindowDays ?? null)
            : null,
        }),
      );
    } else {
      const targetingPayload = buildTargetingPayload(values);
      const multiUse: boolean =
        (values.multiUse ?? false) ||
        targetingPayload.assignmentType !== EDiscountAssignmentType.NONE;

      dispatch(
        discountSave({
          discountType: values.discountType,
          value: values.value,
          description: values.description,
          code: values.code || undefined,
          validFrom: values.validFrom ? values.validFrom.toDate() : null,
          validTo: values.validTo ? values.validTo.endOf('day').toDate() : null,
          multiUse,
          singleUsePerCustomer: multiUse
            ? (values.singleUsePerCustomer ?? true)
            : undefined,
          maxRedemptions: multiUse
            ? (values.maxRedemptions ?? null)
            : undefined,
          combinable: multiUse ? (values.combinable ?? false) : undefined,
          minOrderValue: values.minOrderValue ?? null,
          eligibleCountryTypeIsoCodes:
            values.eligibleCountryTypeIsoCodes ?? null,
          shippingFreeWeightKg: isShippingType
            ? (values.shippingFreeWeightKg ?? null)
            : null,
          referrerRewardDiscountTypeUUID: isReferralType
            ? values.referrerRewardDiscountTypeUuid
            : undefined,
          referrerRewardValue: isReferralType
            ? (values.referrerRewardValue ?? null)
            : null,
          referralMinOrderValue: isReferralType
            ? (values.referralMinOrderValue ?? null)
            : null,
          referralValidationWindowDays: isReferralType
            ? (values.referralValidationWindowDays ?? null)
            : null,
          ...targetingPayload,
        }),
      );
    }

    onClose();
  };

  return (
    <Modal
      title={isEditing ? 'Edit coupon' : 'Create coupon'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {editingDiscount && (
          <Descriptions
            bordered
            size="small"
            column={1}
            style={{ marginBottom: SPACING.lg }}
            items={[
              {
                key: 'code',
                label: 'Code',
                children: (
                  <Space size={SPACING.sm}>
                    {editingDiscount.code}
                    <CopyToClipboard
                      text={editingDiscount.code}
                      size="small"
                      type="text"
                    />
                  </Space>
                ),
              },
              {
                key: 'discount-type',
                label: 'Discount type',
                children: editingDiscount.discountType.code,
              },
              {
                key: 'usage',
                label: 'Usage',
                children: editingDiscount.multiUse
                  ? 'Multi-use'
                  : 'Single-use (one redemption in total)',
              },
              {
                key: 'redemptions',
                label: 'Redemptions',
                children:
                  editingDiscount.multiUse && editingDiscount.maxRedemptions
                    ? `${editingDiscount.redemptionCount} / ${editingDiscount.maxRedemptions}`
                    : editingDiscount.redemptionCount,
              },
              {
                key: 'targeting',
                label: 'Targeting',
                children: describeTargeting(editingDiscount),
              },
            ]}
          />
        )}

        {!isEditing && (
          <Form.Item
            name={['discountType', 'uuid']}
            label="Discount type"
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Select type of discount (percentage / fixed amount / shipping)"
              disabled={inProcess}
              options={discountTypes?.map((discountType) => ({
                value: discountType.uuid,
                label: discountType.code,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          name="value"
          label={
            isShippingType
              ? 'Value — % off shipping (set 0 if only using the free weight allowance below)'
              : isReferralType
                ? 'Referee reward — % off the friend’s order when they use the code'
                : 'Value'
          }
          rules={[{ required: true }, { type: 'number' }]}
        >
          <InputNumber style={{ width: '100%' }} disabled={inProcess} />
        </Form.Item>

        {isShippingType && (
          <Form.Item
            name="shippingFreeWeightKg"
            label="Free shipping weight allowance in kg (optional, e.g. 1 — the first 1kg of the order ships free, the rest is priced normally)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.1}
              disabled={inProcess}
            />
          </Form.Item>
        )}

        {isReferralType && (
          <>
            <Form.Item
              name="referrerRewardDiscountTypeUuid"
              label="Referrer reward type"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select type of the referrer's reward (percentage / fixed amount)"
                disabled={inProcess}
                options={referrerRewardDiscountTypeOptions?.map(
                  (discountType) => ({
                    value: discountType.uuid,
                    label: discountType.code,
                  }),
                )}
              />
            </Form.Item>

            <Form.Item
              name="referrerRewardValue"
              label="Referrer reward — value the referrer gets on their next order once a friend's order qualifies"
              rules={[{ required: true }, { type: 'number' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                disabled={inProcess}
              />
            </Form.Item>

            <Form.Item
              name="referralMinOrderValue"
              label="Minimum order value in EUR for the friend's order to qualify the referrer for a reward (optional)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                disabled={inProcess}
              />
            </Form.Item>

            <Form.Item
              name="referralValidationWindowDays"
              label="Days after delivery before the reward is granted, if the order isn't returned (default 30)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                disabled={inProcess}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true }, { type: 'string', min: 3 }]}
        >
          <Input disabled={inProcess} />
        </Form.Item>

        {!isEditing && (
          <Form.Item name="code" label="Custom code (optional)">
            <Input placeholder="e.g. INFLUENCER5" disabled={inProcess} />
          </Form.Item>
        )}

        <Form.Item name="validFrom" label="Valid from (optional)">
          <DatePicker style={{ width: '100%' }} disabled={inProcess} />
        </Form.Item>

        <Form.Item
          name="validTo"
          label="Valid to (leave empty to never expire)"
        >
          <DatePicker
            style={{ width: '100%' }}
            allowClear
            disabled={inProcess}
          />
        </Form.Item>

        {!isReferralType && (
          <>
            <Form.Item
              name="minOrderValue"
              label="Minimum order value in EUR (optional, e.g. 100 — coupon only usable on orders at or above this)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                disabled={inProcess}
              />
            </Form.Item>

            <Form.Item
              name="eligibleCountryTypeIsoCodes"
              label="Eligible delivery countries (optional, leave empty to allow all)"
            >
              <Select
                mode="multiple"
                showSearch={{ optionFilterProp: 'label' }}
                disabled={inProcess}
                options={getCountryOptions(countries)}
              />
            </Form.Item>

            {!isEditing && (
              <>
                <Form.Item name="targetingMode" label="Targeting">
                  <Radio.Group disabled={inProcess}>
                    <Radio value={ETargetingMode.NONE}>
                      Public (anyone can use)
                    </Radio>
                    <Radio value={ETargetingMode.INDIVIDUAL}>
                      Specific customers
                    </Radio>
                    <Radio value={ETargetingMode.COUNTRY}>
                      Country segment
                    </Radio>
                    <Radio value={ETargetingMode.ORDER_HISTORY}>
                      Order-history segment
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                {targetingModeWatch === ETargetingMode.INDIVIDUAL && (
                  <Form.Item
                    name="individualCustomerUuids"
                    label="Customers"
                    rules={[{ required: true }]}
                  >
                    <CustomerPicker disabled={inProcess} />
                  </Form.Item>
                )}

                {(targetingModeWatch === ETargetingMode.COUNTRY ||
                  targetingModeWatch === ETargetingMode.ORDER_HISTORY) && (
                  <Form.Item
                    name="countryTypeIsoCode"
                    label={
                      targetingModeWatch === ETargetingMode.COUNTRY
                        ? 'Country'
                        : 'Country (optional, combined with order history)'
                    }
                    rules={
                      targetingModeWatch === ETargetingMode.COUNTRY
                        ? [{ required: true }]
                        : undefined
                    }
                  >
                    <Select
                      showSearch={{ optionFilterProp: 'label' }}
                      disabled={inProcess}
                      options={getCountryOptions(countries)}
                    />
                  </Form.Item>
                )}

                {targetingModeWatch === ETargetingMode.ORDER_HISTORY && (
                  <>
                    <Form.Item
                      name="minOrderCount"
                      label="Minimum number of orders (optional)"
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        disabled={inProcess}
                      />
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(
                        prev: IDiscountFormValues,
                        next: IDiscountFormValues,
                      ): boolean => prev.minOrderCount !== next.minOrderCount}
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('minOrderCount') ? (
                          <Form.Item
                            name="orderCountWindowDays"
                            label="Within the last N days"
                            rules={[{ required: true }]}
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              min={1}
                              disabled={inProcess}
                            />
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>

                    <Form.Item
                      name="productTargetMode"
                      label="Product / category ordered (optional)"
                    >
                      <Radio.Group disabled={inProcess}>
                        <Radio value={EProductTargetMode.NONE}>
                          Not filtered
                        </Radio>
                        <Radio value={EProductTargetMode.PRODUCT}>
                          Specific product
                        </Radio>
                        <Radio value={EProductTargetMode.CATEGORY}>
                          Specific category
                        </Radio>
                      </Radio.Group>
                    </Form.Item>

                    {productTargetModeWatch === EProductTargetMode.PRODUCT && (
                      <Form.Item
                        name="productUuid"
                        label="Product"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch={{ optionFilterProp: 'label' }}
                          disabled={inProcess}
                          options={productsShort?.map((product) => ({
                            value: product.uuid,
                            label:
                              getTranslation(product.names, languageType) ||
                              product.uuid,
                          }))}
                        />
                      </Form.Item>
                    )}

                    {productTargetModeWatch === EProductTargetMode.CATEGORY && (
                      <Form.Item
                        name="categoryUuid"
                        label="Category"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch={{ optionFilterProp: 'label' }}
                          disabled={inProcess}
                          options={categories?.map((category) => ({
                            value: category.uuid,
                            label:
                              getTranslation(category.names, languageType) ||
                              category.uuid,
                          }))}
                        />
                      </Form.Item>
                    )}

                    {productTargetModeWatch !== EProductTargetMode.NONE && (
                      <Form.Item
                        name="productWindowDays"
                        label="Within the last N days"
                        rules={[{ required: true }]}
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={1}
                          disabled={inProcess}
                        />
                      </Form.Item>
                    )}
                  </>
                )}
              </>
            )}

            {!isEditing && !isTargeted && (
              <Form.Item name="multiUse" valuePropName="checked">
                <Checkbox disabled={inProcess}>
                  Usable by multiple customers
                </Checkbox>
              </Form.Item>
            )}

            {isMultiUse && (
              <>
                <Form.Item name="singleUsePerCustomer" valuePropName="checked">
                  <Checkbox disabled={inProcess}>
                    Limit to one redemption per customer
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="maxRedemptions"
                  label="Max total redemptions (optional, e.g. 1000)"
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    disabled={inProcess}
                  />
                </Form.Item>

                <Form.Item name="combinable" valuePropName="checked">
                  <Checkbox disabled={inProcess}>
                    Combinable with other combinable coupons
                  </Checkbox>
                </Form.Item>
              </>
            )}
          </>
        )}

        <Button
          type="primary"
          htmlType="submit"
          disabled={inProcess}
          loading={inProcess}
        >
          {isEditing ? 'Save changes' : 'Create'}
        </Button>
      </Form>
    </Modal>
  );
}

export default DiscountForm;
